import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
    addDoc,
    collection,
    doc,
    getDoc,
    getDocs,
    increment,
    limit,
    onSnapshot,
    orderBy,
    query,
    serverTimestamp,
    startAfter,
    where,
    updateDoc,
} from "firebase/firestore";

import InfiniteScroll from "react-infinite-scroll-component";
import PerfectScrollbar from "react-perfect-scrollbar";
import { useDispatch, useSelector } from "react-redux";
import moment from "moment";
import DOMPurify from "dompurify";
import { debounce } from "lodash";

import { useFirebase } from "../../contexts/FirebaseContext";
import { AppDispatch, RootState } from "../../redux-stores/store";
import { setBGWhite, setHeader, setShowFooterNav, showHeader } from "../../redux-stores/slices/layoutSlice";
import { disablePullToRefresh, enablePullToRefresh } from "../../hooks/usePullToRefresh";
import InitialAvatar from "react-avatar";

import { EntityChatRoom } from "../../types/chat";
import { Message as MessageInterface } from "../../types/collaborations/chat";

import "./style.scss";
import RoomMessages from "./RoomMessages";
import { defaultAppHeaderProps } from "../../types/layout/layoutTypes";
import { Link } from "react-router-dom";
import SponsorshipsIcon from "../../assets/icons/SponsorshipsIcon";

const PAGE_SIZE = 20;

function toPlainText(input: string) {
    return (input ?? "")
        .replace(/&nbsp;/g, " ")
        .replace(/<br\s*\/?>/gi, " ")
        .replace(/<[^>]*>/g, " ")
        .replace(/\s+/g, " ")
        .trim();
}

function formatChatListDateLabel(timestamp: any) {
    const date = timestamp?.toDate ? timestamp.toDate() : timestamp ? new Date(timestamp) : null;
    if (!date || Number.isNaN(date.getTime())) return "";

    const m = moment(date);
    if (m.isSame(moment(), "day")) return "Today";
    if (m.isSame(moment().subtract(1, "day"), "day")) return "Yesterday";
    if (m.isAfter(moment().subtract(6, "days"), "day")) return m.format("dddd");
    return m.format("DD/MM/YYYY");
}

function getChatPrimaryText(room: EntityChatRoom) {
    const campaignName = room?.campaign?.name ?? "";
    const brandName = room?.campaign?.brand?.name ?? "";
    if (brandName) return `${campaignName} - ${brandName}`;
    return campaignName || "Chat";
}

function getChatSecondaryText(room: EntityChatRoom) {
    const text = room?.lastMessage?.text ?? "";
    return toPlainText(text) || "No messages yet";
}

export default function Chat() {
    const { uuid: activeRoomId } = useParams();
    const navigate = useNavigate();
    const dispatch: AppDispatch = useDispatch();
    const influencerId = useSelector((state: RootState) => state.influencer.influencer?.uuid);

    const { firebaseUser, db, auth, appUser } = useFirebase();

    const [chatRooms, setChatRooms] = useState<EntityChatRoom[]>([]);
    const [entityChatRoom, setEntityChatRoom] = useState<EntityChatRoom | null>(null);
    const [listLoaded, setListLoaded] = useState(false);
    const [hasMore, setHasMore] = useState(true);
    const lastVisibleRef = useRef<any>(null);

    const [queryText, setQueryText] = useState("");
    const filteredChatRooms = useMemo(() => {
        const q = queryText.trim().toLowerCase();
        if (!q) return chatRooms;
        return chatRooms.filter((room) => {
            const brand = room?.campaign?.brand?.name?.toLowerCase() ?? "";
            const campaign = room?.campaign?.name?.toLowerCase() ?? "";
            return brand.includes(q) || campaign.includes(q);
        });
    }, [chatRooms, queryText]);

    // Messages/detail state
    const [participants, setParticipants] = useState<Record<string, any>>({});
    const [participantsIDs, setParticipantsIDs] = useState<string[]>([]);
    const [rawMessages, setRawMessages] = useState<any[]>([]);
    const [messages, setMessages] = useState<Array<MessageInterface>>([]);
    const [groupedMessages, setGroupedMessages] = useState<Record<string, MessageInterface[]>>({});
    const [input, setInput] = useState("");
    const [isAtBottom, setIsAtBottom] = useState(false);

    const detailScrollRef = useRef<HTMLDivElement | null>(null);
    const bottomSentinelRef = useRef<HTMLDivElement | null>(null);
    const composerRef = useRef<HTMLDivElement | null>(null);

    useEffect(() => {
        dispatch(setBGWhite(true));
        disablePullToRefresh();

        // Keep behavior consistent across mobile webviews
        setTimeout(() => window.dispatchEvent(new Event("resize")), 0);

        return () => {
            dispatch(setBGWhite(false));
            enablePullToRefresh();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (!activeRoomId) return;
        dispatch(setShowFooterNav(false));
        dispatch(showHeader(true));

        return () => {
            dispatch(setShowFooterNav(true));
            dispatch(showHeader(true));
        };
    }, [activeRoomId, dispatch]);

    // Chat list: first page (real-time)
    useEffect(() => {
        if (!influencerId || !firebaseUser || !db) return;

        const q = query(
            collection(db, "entityChatRooms", influencerId, "rooms"),
            orderBy("lastMessage.timestamp", "desc"),
            limit(PAGE_SIZE)
        );

        const unsubscribe = onSnapshot(q, (snapshot) => {
            if (!snapshot.empty) {
                const docs = snapshot.docs.map((d) => ({
                    ...(d.data() as EntityChatRoom),
                    uuid: d.id,
                }));
                setChatRooms(docs);
                lastVisibleRef.current = snapshot.docs[snapshot.docs.length - 1];
                setHasMore(snapshot.docs.length === PAGE_SIZE);
            } else {
                setChatRooms([]);
                setHasMore(false);
            }
            setListLoaded(true);
        });

        return () => unsubscribe();
    }, [db, firebaseUser, influencerId]);

    // Chat list: next page
    const fetchNextPage = useCallback(async () => {
        const lastVisible = lastVisibleRef.current;
        if (!lastVisible || !db || !influencerId) return;

        const q = query(
            collection(db, "entityChatRooms", influencerId, "rooms"),
            orderBy("lastMessage.timestamp", "desc"),
            startAfter(lastVisible),
            limit(PAGE_SIZE)
        );

        const snapshot = await getDocs(q);
        if (!snapshot.empty) {
            const docs = snapshot.docs.map((d) => ({
                ...(d.data() as EntityChatRoom),
                uuid: d.id,
            }));
            setChatRooms((prev) => [...prev, ...docs]);
            lastVisibleRef.current = snapshot.docs[snapshot.docs.length - 1];
            setHasMore(snapshot.docs.length === PAGE_SIZE);
        } else {
            setHasMore(false);
        }
    }, [db, influencerId]);

    // Keep detail room in sync with URL
    useEffect(() => {
        if (!activeRoomId) {
            setEntityChatRoom(null);
            return;
        }
        const active = chatRooms.find((room) => room.uuid === activeRoomId);
        setEntityChatRoom(active ?? null);
    }, [activeRoomId, chatRooms]);

    // Clear composer when switching rooms
    useEffect(() => {
        if (!activeRoomId) return;
        setInput("");
        if (composerRef.current) composerRef.current.innerHTML = "";
    }, [activeRoomId, entityChatRoom?.uuid]);

    // If not present in list (pagination/race), fetch it explicitly
    const fetchEntityChatRoom = useCallback(async () => {
        if (!firebaseUser || !db || !activeRoomId || !influencerId) return;
        try {
            const roomDoc = await getDoc(doc(db, "entityChatRooms", influencerId, "rooms", activeRoomId));
            if (roomDoc.exists()) {
                setEntityChatRoom({ ...(roomDoc.data() as EntityChatRoom), uuid: activeRoomId } as EntityChatRoom);
            }
        } catch (error) {
            console.error("Failed to fetch entity chat room:", error);
        }
    }, [activeRoomId, db, firebaseUser, influencerId]);

    useEffect(() => {
        if (!listLoaded || entityChatRoom || !firebaseUser || !db || !activeRoomId || !influencerId) return;
        fetchEntityChatRoom();
    }, [activeRoomId, db, entityChatRoom, fetchEntityChatRoom, firebaseUser, influencerId, listLoaded]);

    // Subscribe to messages
    useEffect(() => {
        if (!entityChatRoom || !firebaseUser || !db) return;

        const roomId = entityChatRoom.uuid;
        const q = query(collection(db, "chatRooms", roomId, "messages"), orderBy("timestamp", "asc"));

        const unsubscribe = onSnapshot(q, (snapshot) => {
            const newMessages = snapshot.docs
                .map((docSnap) => ({
                    id: docSnap.id,
                    ...docSnap.data(),
                }))
                .filter((m: any) => m.senderId);

            const senderIdsArray = newMessages.map((m: any) => m.senderId);
            const uniqueSenderIds: any[] = [];
            for (let i = 0; i < senderIdsArray.length; i++) {
                if (uniqueSenderIds.indexOf(senderIdsArray[i]) === -1) uniqueSenderIds.push(senderIdsArray[i]);
            }
            setParticipantsIDs(uniqueSenderIds);
            setRawMessages(newMessages);
        });

        return () => unsubscribe();
    }, [db, entityChatRoom, firebaseUser]);

    // Fetch participants
    useEffect(() => {
        if (participantsIDs.length === 0 || !db) return;

        const fetchParticipants = async () => {
            const usersRef = collection(db, "users");
            const idsToFetch = participantsIDs.filter((id) => !participants[id]);
            if (idsToFetch.length === 0) return;

            const batches: string[][] = [];
            for (let i = 0; i < idsToFetch.length; i += 10) batches.push(idsToFetch.slice(i, i + 10));

            const newParticipants: Record<string, any> = {};
            for (const batch of batches) {
                const q = query(usersRef, where("__name__", "in", batch));
                const snapshot = await getDocs(q);
                snapshot.forEach((docSnap) => {
                    newParticipants[docSnap.id] = docSnap.data();
                });
            }

            if (Object.keys(newParticipants).length > 0) {
                setParticipants((prev) => ({ ...prev, ...newParticipants }));
            }
        };

        fetchParticipants();
    }, [db, participants, participantsIDs]);

    const getMessageGroupKey = useCallback(
        (msg: { senderId?: string }) => {
            const sender = participants[msg.senderId ?? ""];
            if (sender?.access_type === "hub") return `hub:${msg.senderId}`;
            if (msg.senderId === auth.currentUser?.uid) return `out:${msg.senderId}`;
            return `in:${msg.senderId}`;
        },
        [auth.currentUser?.uid, participants]
    );

    const getMessagePosition = (currentMsg: any, lastMsg: any, nextMsg: any) => {
        const currentKey = getMessageGroupKey(currentMsg);
        const lastKey = lastMsg ? getMessageGroupKey(lastMsg) : null;
        const nextKey = nextMsg ? getMessageGroupKey(nextMsg) : null;

        if ((!lastKey || lastKey !== currentKey) && (!nextKey || nextKey !== currentKey)) {
            return "single";
        }
        if ((!lastKey || lastKey !== currentKey) && nextKey === currentKey) {
            return "first";
        }
        if (lastKey === currentKey && (!nextKey || nextKey !== currentKey)) {
            return "last";
        }
        if (lastKey === currentKey && nextKey === currentKey) {
            return "normal";
        }
        return "single";
    };

    const formatSenderName = (sender: { name?: string; access_type?: string } | undefined, fallback: string) => {
        const baseName = sender?.name ?? fallback;
        return sender?.access_type === "hub" ? `${baseName} [Manager]` : baseName;
    };

    const transformMessages = useCallback(
        (raw: any[]): Array<MessageInterface> => {
            return raw.map((msg, i) => {
                const lastMsg = raw[i - 1];
                const nextMsg = raw[i + 1];
                const position = getMessagePosition(msg, lastMsg, nextMsg);
                const sender = participants[msg.senderId];
                const isHubMessage = sender?.access_type === "hub";
                const isOwnMessage = msg.senderId === auth.currentUser?.uid;
                const direction = isHubMessage || isOwnMessage ? "outgoing" : "incoming";

                return {
                    type: "message",
                    props: {
                        model: {
                            message: msg.text ?? "",
                            sentTime: msg.timestamp?.toDate ? moment(msg.timestamp.toDate()).format("HH:mm") : "",
                            sender: formatSenderName(sender, msg.senderId),
                            senderProfile: sender?.profile_photo_url,
                            direction,
                            isHubMessage,
                            position,
                            senderId: msg.senderId,
                            date: msg.timestamp?.toDate ? moment(msg.timestamp.toDate()).startOf("day").toISOString() : "",
                        },
                    },
                };
            });
        },
        [auth.currentUser?.uid, getMessageGroupKey, participants]
    );

    useEffect(() => {
        const transformed = transformMessages(rawMessages);

        const groups: Record<string, MessageInterface[]> = {};
        transformed.forEach((m) => {
            const dateKey = m.props.model.date;
            if (!groups[dateKey]) groups[dateKey] = [];
            groups[dateKey].push(m);
        });

        setMessages(transformed);
        setGroupedMessages(groups);
    }, [rawMessages, transformMessages]);

    const allowedTags = useMemo(
        () => ["b", "i", "u", "strong", "em", "br", "ul", "ol", "li", "h1", "h2", "h3", "h4", "h5", "h6", "p", "a"],
        []
    );
    const allowedAttr = useMemo(() => ["href", "rel", "target"], []);

    const handlePaste = (e: React.ClipboardEvent<HTMLDivElement>) => {
        e.preventDefault();
        const html = e.clipboardData.getData("text/html");
        const text = e.clipboardData.getData("text/plain");

        const cleanHTML = html
            ? DOMPurify.sanitize(html, { ALLOWED_TAGS: allowedTags, ALLOWED_ATTR: allowedAttr })
            : text.replace(/\n/g, "<br>");

        const current = composerRef.current?.innerHTML ?? "";
        const next = current + cleanHTML;
        if (composerRef.current) composerRef.current.innerHTML = next;
        setInput(next);
    };

    const sendMessage = async () => {
        const normalizedInput = (input ?? "")
            // Normalize common contentEditable wrappers into <br> so rendering preserves line breaks
            .replace(/<\/div>\s*<div>/gi, "<br>")
            .replace(/<div>\s*<br\s*\/?>\s*<\/div>/gi, "<br>")
            .replace(/<\/?div>/gi, "");

        const plain = normalizedInput
            .replace(/<br\s*\/?>/gi, "\n")
            .replace(/&nbsp;/g, " ")
            .replace(/<[^>]*>/g, " ")
            .trim();

        if (!plain || !auth.currentUser || !entityChatRoom?.uuid || !appUser || entityChatRoom.access !== "w") return;

        const timestamp = serverTimestamp();
        const roomId = entityChatRoom.uuid;

        const message = {
            senderId: auth.currentUser.uid,
            text: normalizedInput,
            timestamp,
            senderEntityId: influencerId,
        };

        await addDoc(collection(db, "chatRooms", roomId, "messages"), message);

        const lastMessage = {
            ...message,
            senderName: appUser.name,
            senderProfileUrl: appUser.profile_photo_url,
        };

        const chatRoomRef = doc(db, "chatRooms", roomId);
        updateDoc(chatRoomRef, { lastMessage });

        if (Array.isArray(entityChatRoom?.entities)) {
            for (const entityId of entityChatRoom.entities) {
                const entityChatRef = doc(db, "entityChatRooms", entityId, "rooms", roomId);
                updateDoc(entityChatRef, { lastMessage, ...(entityId !== influencerId ? { unreadCount: increment(1) } : {}) });
            }
        }

        setInput("");
        if (composerRef.current) composerRef.current.innerHTML = "";
    };

    const setLastRead = useCallback(async () => {
        if (messages.length === 0 || !entityChatRoom?.uuid || !influencerId || !db) return;
        if ((entityChatRoom.unreadCount ?? 0) <= 0) return;

        const roomId = entityChatRoom.uuid;
        const entityChatRef = doc(db, "entityChatRooms", influencerId, "rooms", roomId);
        await updateDoc(entityChatRef, { unreadCount: 0 });
    }, [db, entityChatRoom?.unreadCount, entityChatRoom?.uuid, influencerId, messages.length]);

    const setLastReadDebounced = useMemo(() => debounce(setLastRead, 2000), [setLastRead]);
    useEffect(() => () => setLastReadDebounced.cancel(), [setLastReadDebounced]);

    useEffect(() => {
        if (!activeRoomId) return;
        const rootEl = detailScrollRef.current;
        const targetEl = bottomSentinelRef.current;
        if (!rootEl || !targetEl) return;

        const observer = new IntersectionObserver(
            (entries) => {
                const intersecting = entries.some((e) => e.isIntersecting);
                setIsAtBottom(intersecting);
                if (intersecting) {
                    setLastReadDebounced();
                }
            },
            { root: rootEl, threshold: 0.1 }
        );

        observer.observe(targetEl);
        return () => observer.disconnect();
    }, [activeRoomId, setLastReadDebounced, entityChatRoom?.uuid]);

    const scrollToBottom = useCallback(
        (behavior: ScrollBehavior) => {
            const el = detailScrollRef.current;
            if (!el) return;
            el.scrollTo({ top: el.scrollHeight, behavior });
        },
        []
    );

    // On open / first render: jump to latest message after layout.
    useLayoutEffect(() => {
        if (!activeRoomId) return;
        if (rawMessages.length === 0) return;

        // Wait a tick so bubble layout has its final height.
        requestAnimationFrame(() => {
            requestAnimationFrame(() => {
                scrollToBottom("auto");
            });
        });
    }, [activeRoomId, rawMessages.length, scrollToBottom]);

    // While user is at bottom: keep pinned as new messages arrive.
    useEffect(() => {
        if (!activeRoomId) return;
        if (!isAtBottom) return;
        if (rawMessages.length === 0) return;
        scrollToBottom("smooth");
    }, [activeRoomId, isAtBottom, rawMessages.length, scrollToBottom]);

    useEffect(() => {
        if (!activeRoomId) return;
        if (!isAtBottom) return;
        if (messages.length === 0) return;
        setLastReadDebounced();
    }, [activeRoomId, isAtBottom, messages.length, setLastReadDebounced]);

    const showList = !activeRoomId;

    useEffect(() => {
        // For chat/:uuid, drive the global `AppHeader` from Redux.
        // No custom header is rendered in this page; we only set header state.
        if (!activeRoomId) {
            dispatch(setHeader({
                generic: false,
                page: {
                    title: "Chat",
                    onBackClick: () => navigate("/"),
                },
            }));
            return () => {
                dispatch(setHeader(defaultAppHeaderProps));
            };
        }

        if (!entityChatRoom?.campaign) return;

        const campaignName = entityChatRoom?.campaign?.name ?? "";
        const brandName = entityChatRoom?.campaign?.brand?.name ?? "";
        const title = brandName ? `${campaignName} - ${brandName}` : campaignName;

        dispatch(
            setHeader({
                generic: false,
                page: {
                    title: title || "Chat",
                    onBackClick: () => navigate("/chat"),
                    rightContent: (
                        <Link to={{ pathname: `/collaborations/${entityChatRoom?.campaign.uuid}` }} className="d-flex align-items-center w-100 justify-content-end gap-2 fs-10 fw-500 text-primary">
                            <SponsorshipsIcon color="var(--primary)" size={16} />
                            Open Collab
                        </Link>
                    ),
                },
            })
        );

        return () => {
            dispatch(setHeader(defaultAppHeaderProps));
        };
    }, [activeRoomId, dispatch, entityChatRoom?.campaign, navigate]);

    return (
        <div className="tt-chat-page app-page p-0">
            {showList ? (
                <PerfectScrollbar id="tt-chat-scroll">
                    {/* <div className="tt-chat-search tt-chat-search--sticky">
                        <div className="tt-chat-search-inner">
                            <i className="fas fa-search tt-chat-search-icon" />
                            <input
                                className="tt-chat-search-input"
                                value={queryText}
                                onChange={(e) => setQueryText(e.target.value)}
                                placeholder="Search by brand"
                                aria-label="Search by brand"
                            />
                            {queryText.trim() ? (
                                <button
                                    type="button"
                                    className="tt-chat-search-clear"
                                    aria-label="Clear search"
                                    onClick={() => setQueryText("")}
                                >
                                    <i className="fas fa-times" />
                                </button>
                            ) : null}
                        </div>
                    </div> */}

                    <InfiniteScroll
                        dataLength={filteredChatRooms.length}
                        next={fetchNextPage}
                        className="tt-chat-list"
                        hasMore={hasMore}
                        scrollableTarget="tt-chat-scroll"
                        loader={<></>}
                    >
                        {filteredChatRooms.map((room) => (
                            <div
                                key={room.uuid}
                                className="tt-chat-row"
                                role="button"
                                tabIndex={0}
                                onClick={() => navigate(`/chat/${room.uuid}`)}
                                onKeyDown={(e) => {
                                    if (e.key === "Enter" || e.key === " ") navigate(`/chat/${room.uuid}`);
                                }}
                            >
                                <div className="tt-chat-row-avatar">
                                    <InitialAvatar
                                        name={room?.campaign?.brand?.name ?? room?.campaign?.name ?? ""}
                                        maxInitials={1}
                                        size="44"
                                        round
                                    />
                                </div>

                                <div className="tt-chat-row-content">
                                    <div className="tt-chat-row-top">
                                        <div className="tt-chat-row-title">{getChatPrimaryText(room)}</div>
                                        <div className="tt-chat-row-date">{formatChatListDateLabel(room.lastMessage?.timestamp)}</div>
                                    </div>

                                    <div className="tt-chat-row-bottom">
                                        <div className="tt-chat-row-subtitle">{getChatSecondaryText(room)}</div>
                                        {(room?.unreadCount ?? 0) > 0 ? (
                                            <div className="tt-chat-unread-pill" aria-label={`${room.unreadCount} unread`}>
                                                {room.unreadCount}
                                            </div>
                                        ) : null}
                                    </div>
                                </div>
                            </div>
                        ))}

                        {(filteredChatRooms.length === 0) ? (
                            <div className="tt-chat-empty">
                                <div className="tt-chat-empty-box">
                                    <h3>{queryText.trim() ? "No matches found!" : "No recent chats!"}</h3>
                                </div>
                            </div>
                        ) : null}
                    </InfiniteScroll>
                </PerfectScrollbar>
            ) : (
                <div className="tt-chat-detail">
                    {entityChatRoom ? (
                        <div className="tt-chat-detail-layout">
                            <div className="tt-chat-detail-scroll" ref={detailScrollRef}>
                                <RoomMessages groupedMessages={groupedMessages} />
                                <div ref={bottomSentinelRef} className="bottom-sentinel" />
                            </div>
                            <div className="tt-chat-composer-wrapper">
                                <div className="tt-chat-composer">
                                    {
                                        entityChatRoom.access !== "w" ? (
                                                <div className="tt-chat-composer-editor text-center text-muted w-100">
                                                    <p className="mb-0">You don't have permission to send messages.</p>
                                                </div>
                                        ) : (
                                            <>
                                            <div className="tt-chat-composer-input">
                                        <div
                                            ref={composerRef}
                                            className={`tt-chat-composer-editor ${entityChatRoom.access !== "w" ? "tt-chat-composer-editor--disabled" : ""}`}
                                            contentEditable={entityChatRoom.access === "w"}
                                            suppressContentEditableWarning
                                            onInput={() => {
                                                if (!composerRef.current) return;
                                                const el = composerRef.current;
                                                const text = (el.textContent ?? "").replace(/\u00A0/g, " ").trim();

                                                // contentEditable often leaves a <br> or whitespace node behind,
                                                // which prevents :empty placeholder CSS from activating.
                                                if (!text) {
                                                    el.innerHTML = "";
                                                    setInput("");
                                                    return;
                                                }

                                                setInput(el.innerHTML);
                                            }}
                                            onPaste={handlePaste}
                                            onKeyDown={(e) => {
                                                if (e.key === "Enter" && !e.shiftKey) {
                                                    e.preventDefault();
                                                    void sendMessage();
                                                }
                                            }}
                                            data-placeholder="Type your message..."
                                        />
                                    </div>

                                    <button
                                        type="button"
                                        className="tt-chat-composer-send"
                                        disabled={entityChatRoom.access !== "w"}
                                        aria-label="Send message"
                                        onClick={() => void sendMessage()}
                                    >
                                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                                            <path fillRule="evenodd" clipRule="evenodd" d="M20.3521 10.5208L18.6357 15.6701C17.4255 19.3008 16.8204 21.1161 15.933 21.6319C15.0889 22.1227 14.0463 22.1227 13.2022 21.6319C12.3148 21.1161 11.7097 19.3008 10.4995 15.6701C10.3052 15.0872 10.208 14.7957 10.0449 14.5521C9.88687 14.316 9.68404 14.1131 9.44793 13.9551C9.2043 13.792 8.91282 13.6948 8.32987 13.5005C4.69923 12.2903 2.88392 11.6852 2.36806 10.7978C1.87731 9.95369 1.87731 8.91112 2.36806 8.06698C2.88392 7.17964 4.69923 6.57453 8.32987 5.36432L13.4792 3.64788C17.9776 2.14842 20.2268 1.39869 21.414 2.58595C22.6013 3.77322 21.8516 6.02242 20.3521 10.5208ZM13.0457 10.9022C12.7544 10.6077 12.7571 10.1328 13.0516 9.84153L17.2621 5.67742C17.5566 5.38615 18.0315 5.38878 18.3227 5.6833C18.614 5.97781 18.6114 6.45267 18.3169 6.74394L14.1063 10.9081C13.8118 11.1993 13.337 11.1967 13.0457 10.9022Z" fill="var(--primary)" />
                                        </svg>
                                    </button>
                                            </>
                                        )
                                    }
                                </div>
                            </div>
                        </div>
                    ) : (
                        <div className="tt-chat-detail-empty">
                            <div className="tt-chat-empty-box">
                                <h3>No Chat Selected!</h3>
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

